import AutoCompleteParticipantModal from '@/components/modals/autocomplete-participant-modal/AutoCompleteParticipantModal';
import CalendarModal from '@/components/modals/calendar-modal/CalendarModal';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import useMixPanel from '@/hooks/useMixPanel';
import { TUserData } from '@/services/api/user-api';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IdentificationTypesSelect from '../../input/identification-types-select/IdentificationTypesSelect';

type ParticipantWingfoilProps = {
  form: UseFormReturn<any, any, undefined>;
  user: TUserData | undefined;
  handleBlur: () => void;
};

const ParticipantWingfoil: React.FC<ParticipantWingfoilProps> = ({
  form,
  user,
  handleBlur
}) => {
  const Mixpanel = useMixPanel();
  const { t } = useTranslation(['newBooking', 'common']);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<string>('');

  const userConsumerData = user?.consumers?.find(
    (consumer) => consumer.isOwnUser
  );

  const userIsParticipant = form.watch('userIsParticipant');
  const participantFormValue = form.getValues().participant;
  const participantAvailable = user?.consumers?.filter(
    (consumer) => !participantFormValue?.id && !consumer?.isOwnUser
  );
  const isSelectedExistingParticipant = Boolean(participantFormValue?.id);
  const disabledParticipantInputs = isSelectedExistingParticipant;

  const handleSelectAutoCompleteParticipant = (id: string) => {
    Mixpanel.track('Action: Select participant from autocomplete', {
      screen: 'New Booking',
      organization: 'Windsurf Area - Campus nautico',
      participantId: id
    });

    const participant = user?.consumers?.find((consumer) => consumer.id === id);
    if (participant) {
      const participantFormValue = {
        id: participant.id,
        firstName: participant.firstName,
        lastName: participant.lastName,
        identification: {
          number: participant.identification?.number || '',
          type: participant.identification?.type || ''
        },
        address: participant.address || '',
        location: participant.location || '',
        postalCode: participant.postalCode || '',
        birthdate: participant.birthdate
          ? new Date(participant.birthdate)
          : undefined,
        illness: {
          value: participant.additionalData?.illness?.value || false,
          answer: participant.additionalData?.illness?.answer || ''
        }
      };

      // First we update the participant field
      form.setValue('participant', participantFormValue, {
        shouldValidate: true
      });

      // Then we update specifically the illness field
      form.setValue('participant.illness', participantFormValue.illness, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    }
    setIsOpenDialog(false);
  };

  const removeSelectedParticipant = async () => {
    Mixpanel.track('Action: Remove selected participant', {
      screen: 'New Booking',
      organization: 'Windsurf Area - Campus nautico'
    });

    form.reset({
      ...form.getValues(),
      participant: {
        id: '',
        firstName: '',
        lastName: '',
        identification: {
          number: '',
          type: ''
        },
        address: '',
        location: '',
        postalCode: '',
        birthdate: undefined,
        illness: {
          value: false,
          answer: ''
        }
      }
    });
    form.clearErrors();
  };

  return (
    <div className="flex flex-col gap-8">
      {/* CHECKBOX SI PARTICIPANTE ES MENOR */}

      <FormField
        control={form.control}
        name="userIsParticipant"
        render={({ field }) => (
          <FormItem>
            <div className="space-y-3 p-2">
              <FormControl>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="userIsParticipant"
                    checked={!field.value}
                    onCheckedChange={() => {
                      form.clearErrors();
                      field.onChange(!field.value);
                      Mixpanel.track(
                        'Press checkbox to select if user is participant',
                        {
                          screen: 'New Booking',
                          organization: 'Windsurf Area - Wingfoil',
                          value: !field.value
                        }
                      );
                    }}
                    className="w-5 h-5"
                  />
                  <FormLabel
                    htmlFor="userIsParticipant"
                    className="text-base font-bold"
                  >
                    {t('stepParticipants.userIsParticipantAndUnderage')}
                  </FormLabel>
                </div>
              </FormControl>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      {/* USER: Datos del padre/madre/tutor (si no es participante) */}

      <div className="border border-primary p-6 rounded-md space-y-4">
        <h3 className="font-bold">
          {userIsParticipant
            ? t('newBooking:titleStudent')
            : t('newBooking:titleParent')}
        </h3>

        {/* Nombre */}
        <FormField
          control={form.control}
          name="user.firstName"
          render={({ field }) => {
            const disabled = !!userConsumerData?.firstName;
            return (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.firstName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user first name input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Wingfoil'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Apellido */}
        <FormField
          control={form.control}
          name="user.lastName"
          render={({ field }) => {
            const disabled = !!userConsumerData?.lastName;
            return (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.lastName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user last name input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Wingfoil'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Teléfono */}
        <FormField
          control={form.control}
          name="user.phone"
          render={({ field }) => {
            const disabled = !!userConsumerData?.phone;
            return (
              <FormItem>
                <FormLabel>{t('newBooking:phone')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user phone input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Wingfoil'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="user.identification.type"
          render={({ field }) => {
            const disabled = !!userConsumerData?.identification?.type;
            return (
              <FormItem>
                <IdentificationTypesSelect
                  defaultValue={field?.value}
                  onValueChange={(value) => {
                    Mixpanel.track('Selected identification type for user', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Wingfoil',
                      value
                    });
                    field.onChange(value);
                  }}
                  disabled={disabled}
                  field={field}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="user.identification.number"
          render={({ field }) => {
            const disabled = !!userConsumerData?.identification?.number;
            return (
              <FormItem>
                <FormLabel>
                  {t('stepParticipants.form.identificationNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onClick={() =>
                      Mixpanel.track('press user id input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Wingfoil'
                      })
                    }
                    placeholder={t('stepParticipants.form.id')}
                    value={field?.value ?? ''}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Email */}
        <FormField
          control={form.control}
          name="user.email"
          render={({ field }) => {
            const disabled = !!userConsumerData?.email;
            return (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.mail')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onBlur={handleBlur}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user email input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Wingfoil'
                      })
                    }
                  />
                </FormControl>
                <FormDescription>
                  {t('newBooking:stepParticipants.form.mail_description')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        {userIsParticipant && (
          <>
            {/* Fecha de Nacimiento */}
            <FormField
              control={form.control}
              name="user.birthdate"
              render={({ field }) => {
                const disabled = !!userConsumerData?.birthdate;
                return (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
                    <CalendarModal
                      value={field.value}
                      placeholder={t('stepParticipants.form.birth')}
                      onAccept={(value) => {
                        field.onChange(value);
                      }}
                      disabledInputLabel={disabled}
                      onClickInputLabel={() =>
                        Mixpanel.track('Press user birthday date button', {
                          screen: 'New Booking',
                          organization: 'Windsurf Area - Wingfoil'
                        })
                      }
                      initialFocus
                      dropDownPicker
                    />
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Domicilio */}
            <FormField
              control={form.control}
              name="user.address"
              render={({ field }) => {
                const disabled = !!userConsumerData?.address;
                return (
                  <FormItem>
                    <FormLabel>{t('newBooking:address')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={disabled}
                        onClick={() =>
                          Mixpanel.track('press user address input', {
                            screen: 'New Booking',
                            organization: 'Windsurf Area - Wingfoil'
                          })
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Localidad */}
            <FormField
              control={form.control}
              name="user.location"
              render={({ field }) => {
                const disabled = !!userConsumerData?.location;
                return (
                  <FormItem>
                    <FormLabel>{t('newBooking:location')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Madrid"
                        disabled={disabled}
                        onClick={() =>
                          Mixpanel.track('press user location input', {
                            screen: 'New Booking',
                            organization: 'Windsurf Area - Wingfoil'
                          })
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* CP */}
            <FormField
              control={form.control}
              name="user.postalCode"
              render={({ field }) => {
                const disabled = !!userConsumerData?.postalCode;
                return (
                  <FormItem>
                    <FormLabel>{t('newBooking:postalCode')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={disabled}
                        onClick={() =>
                          Mixpanel.track('press user postal code input', {
                            screen: 'New Booking',
                            organization: 'Windsurf Area - Wingfoil'
                          })
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Enfermedad */}

            <FormField
              control={form.control}
              name="user.illness"
              render={({ field }) => {
                const defaultValue =
                  field?.value?.value === undefined
                    ? undefined
                    : field?.value?.value
                    ? 'yes'
                    : 'no';
                const disabled =
                  !!userConsumerData?.additionalData?.illness?.value;
                return (
                  <FormItem>
                    <div className="flex flex-row items-end gap-5">
                      <FormLabel>{t('newBooking:illness')}</FormLabel>
                      <FormControl>
                        <RadioGroup
                          disabled={disabled}
                          onValueChange={(value) =>
                            field.onChange({
                              ...field.value,
                              value: value === 'yes'
                            })
                          }
                          defaultValue={defaultValue}
                          className="flex space-x-2 mt-0 items-center"
                        >
                          <FormItem className="flex items-center space-x-1 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="yes" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {t('yes')}
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-1 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="no" />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {t('no')}
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                    </div>

                    {field?.value?.value && (
                      <div className="py-1">
                        <Textarea
                          placeholder={
                            field?.value?.placeholder || t('extraInfo')
                          }
                          disabled={disabled}
                          {...field}
                          onChange={(e) =>
                            field.onChange({
                              ...field.value,
                              answer: e.target.value
                            })
                          }
                          value={field?.value?.answer || ''}
                        />
                      </div>
                    )}
                  </FormItem>
                );
              }}
            />
          </>
        )}
      </div>

      {/* Datos del participante */}

      {!userIsParticipant && (
        <div className="border border-primary p-6 rounded-md space-y-4">
          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
            <h3 className="font-bold">{t('newBooking:titleStudent')}</h3>
            <AutoCompleteParticipantModal
              open={isOpenDialog}
              onOpenChange={setIsOpenDialog}
              consumers={participantAvailable}
              selectedParticipant={selectedParticipant}
              setSelectedParticipant={setSelectedParticipant}
              handleSelect={handleSelectAutoCompleteParticipant}
              removeSelectedParticipant={removeSelectedParticipant}
              formValue={participantFormValue}
            />
          </div>
          {/* Nombre */}
          <FormField
            control={form.control}
            name="participant.firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.firstName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Introduce el nombre del alumno"
                    disabled={disabledParticipantInputs}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Primer Apellido */}
          <FormField
            control={form.control}
            name="participant.lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.lastName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Introduce el apellido del alumno"
                    disabled={disabledParticipantInputs}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="participant.identification.type"
            render={({ field }) => {
              return (
                <FormItem>
                  <IdentificationTypesSelect
                    defaultValue={field?.value}
                    disabled={disabledParticipantInputs}
                    onValueChange={(value) => {
                      // Mixpanel.track('press user id input', {
                      //   screen: 'New Booking',
                      //   organization: 'Viento en popa'
                      // });
                      field.onChange(value);
                    }}
                    field={field}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="participant.identification.number"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>
                    {t('stepParticipants.form.identificationNumber')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      // onClick={() =>
                      //   Mixpanel.track(
                      //     'press participants id number input',
                      //     {
                      //       screen: 'New Booking',
                      //       organization: 'Viento en popa'
                      //     }
                      //   )
                      // }
                      placeholder={t('stepParticipants.form.id')}
                      {...field}
                      value={field?.value ?? ''}
                      disabled={disabledParticipantInputs}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Fecha de Nacimiento */}
          <FormField
            control={form.control}
            name="participant.birthdate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
                <CalendarModal
                  value={field.value}
                  placeholder={t('stepParticipants.form.birth')}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  // onClickInputLabel={() =>
                  //   Mixpanel.track('press birthday date button ', {
                  //     screen: 'New Booking'
                  //   })
                  // }
                  initialFocus
                  dropDownPicker
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Domicilio */}
          <FormField
            control={form.control}
            name="participant.address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('newBooking:address')}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={disabledParticipantInputs} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Localidad */}
          <FormField
            control={form.control}
            name="participant.location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('newBooking:location')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Madrid"
                    disabled={disabledParticipantInputs}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* CP */}
          <FormField
            control={form.control}
            name="participant.postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('newBooking:postalCode')}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={disabledParticipantInputs} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Enfermedad */}
          <FormField
            control={form.control}
            name="participant.illness"
            render={({ field }) => {
              const defaultValue =
                field?.value?.value === undefined
                  ? undefined
                  : field?.value?.value
                  ? 'yes'
                  : 'no';

              return (
                <FormItem>
                  <div className="flex flex-row items-end gap-5">
                    <FormLabel>{t('newBooking:illness')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={defaultValue}
                        onValueChange={(value) =>
                          field.onChange({
                            ...field.value,
                            value: value === 'yes'
                          })
                        }
                        defaultValue={defaultValue}
                        disabled={disabledParticipantInputs}
                        className="flex space-x-2 mt-0 items-center"
                      >
                        <FormItem className="flex items-center space-x-1 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="yes" />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t('yes')}
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-1 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="no" />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t('no')}
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                  </div>

                  {field?.value?.value && (
                    <div className="px-2 py-1">
                      <Textarea
                        disabled={disabledParticipantInputs}
                        placeholder={
                          field?.value?.placeholder || t('extraInfo')
                        }
                        {...field}
                        onChange={(e) =>
                          field.onChange({
                            ...field.value,
                            answer: e.target.value
                          })
                        }
                        value={field?.value?.answer ?? ''}
                      />
                    </div>
                  )}
                </FormItem>
              );
            }}
          />
        </div>
      )}

      {/* Persona de contacto */}

      <div className="border border-primary p-6 rounded-md space-y-4">
        <h3 className="font-bold">{t('newBooking:additionalContact')}</h3>

        {/* Nombre y Apellidos */}
        <FormField
          control={form.control}
          name="emergencyContact.fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {`${t('newBooking:stepParticipants.form.firstName')} y ${t(
                  'newBooking:stepParticipants.form.lastName'
                ).toLowerCase()}`}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Teléfono */}

        <FormField
          control={form.control}
          name="emergencyContact.phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('newBooking:phone')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Privacidad de datos  */}
      <FormField
        control={form.control}
        name="acceptDataPrivacy"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-start">
              <FormControl>
                <Checkbox
                  id="isAcceptDataPrivacy"
                  checked={field.value}
                  onCheckedChange={() => field.onChange(!field.value)}
                />
              </FormControl>
              <FormLabel htmlFor="isAcceptDataPrivacy" className="ml-2">
                {t('newBooking:stepParticipants.form.privacyPolicy')}{' '}
                <a
                  // TODO: Remove hardcoded url
                  href="https://windsurfarea.com/politica-privacidad/"
                  target="blank"
                  className="text-primary underline"
                  rel="noreferrer noopener"
                >
                  {t('newBooking:stepParticipants.form.privacyPolicyLink')}
                </a>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Fotografía  */}
      <FormField
        control={form.control}
        name="acceptTakePhotos"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-start">
              <FormControl>
                <Checkbox
                  id="isAcceptTakePhotos"
                  checked={field.value}
                  onCheckedChange={() => field.onChange(!field.value)}
                />
              </FormControl>
              <FormLabel htmlFor="isAcceptTakePhotos" className="ml-2">
                {t('newBooking:stepParticipants.form.takePhotos')}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Ofrecimiento de productos/servicios  */}
      <FormField
        control={form.control}
        name="authOfferProducts"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-start">
              <FormControl>
                <Checkbox
                  id="isAuthOfferProducts"
                  checked={field.value}
                  onCheckedChange={() => field.onChange(!field.value)}
                />
              </FormControl>
              <FormLabel htmlFor="isAuthOfferProducts" className="ml-2">
                {t('newBooking:stepParticipants.form.acceptOffers')}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default ParticipantWingfoil;
